import { Button, Modal, notification, Table } from "antd";
import React, { FC, useMemo, useState } from "react";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { FormInput, Loader } from "shared/ui";
import { handleRequestError } from "shared/helpers";
import { useConfirmDeleteModal, useModal } from "shared/hooks";
import { AdaptedValues } from "shared/types";
import { expandOrdersString, ModalSizeValue } from "shared/constants";
import {
  OrderItem,
  SupplierCompanyItem,
  useDeleteOrderMutation,
  useGetAllCompaniesQuery,
  useGetAllOrdersQuery,
} from "shared/api";
import { useGetCompanyTableColumns } from "shared/hooks/useGetCompaniesTable";
import { AddProductCardValues } from "../ProductCards";
import { useUpdateProductCardMutation } from "../../../../shared/api/productCard/productCardApi";
import { ProcessStepsTable } from "./ProcessStepsTable";
import { ProcessStepItem } from "../../../../shared/api/productCard/models";
import { ProcessStepToTheCardActionsModal } from "./AddProcessStepToTheCardModal";
import { AddCustomerToTheCardModal } from "./AddCustomerToTheCardModal";
import { useGetSimpleOrderTableColumns } from "./useGetOrdersDataForTable";

interface EditProductCardModalProps {
  isOpenModal: boolean;
  hideEditProductCardModal: () => void;
  adaptedSteps: AdaptedValues[];
  processSteps?: ProcessStepItem[];
  isLoadingProcessSteps: boolean;
  customers?: SupplierCompanyItem[];
  adaptedCustomers: AdaptedValues[];
}

const EditProductCardModal: FC<EditProductCardModalProps> = ({
  isOpenModal,
  hideEditProductCardModal,
  adaptedSteps,
  processSteps,
  isLoadingProcessSteps,
  customers,
  adaptedCustomers,
}) => {
  const { values, resetForm, handleBlur, handleChange, isValid, errors } =
    useFormikContext<AddProductCardValues>();

  const { t } = useTranslation();

  const [isOpenAddCompanyModal, showCompanyModal, hideCompanyModal] =
    useModal();

  const [api, contextHolder] = notification.useNotification();

  const [updateProductCard] = useUpdateProductCardMutation();

  const {
    data: companies,
    isLoading: isLoadingCompanies,
    refetch,
  } = useGetAllCompaniesQuery(
    { product_card: values.id! },
    { skip: !values.id }
  );

  const { data: orders, isLoading: isLoadingOrders } = useGetAllOrdersQuery(
    {
      product_card: values.id!,
      expand: expandOrdersString,
    },
    { skip: !values.id }
  );

  const [deleteOrder] = useDeleteOrderMutation();

  const deleteOrderHandler = async (record: OrderItem) => {
    try {
      await deleteOrder(record).unwrap();
      api.success({
        message: t("orders.success"),
        description: t("orders.delete-msg"),
      });
    } catch (error) {
      const description = handleRequestError(error, t("orders.error"));

      api.error({
        message: t("orders.error"),
        description,
      });
    }
  };

  const showDeleteOrderModal = useConfirmDeleteModal(
    t("orders.delete-alert"),
    deleteOrderHandler
  );

  const companyColumns = useGetCompanyTableColumns({
    t,
  });

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const [
    isOpenAddProcessStepModal,
    showProcessStepsModal,
    hideProcessStepsModal,
  ] = useModal();

  const [reorderingIds, setReorderingIds] = useState<number[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);

  const closeModal = () => {
    hideEditProductCardModal();
    resetForm();
  };

  const mergedCompanies = useMemo(() => {
    if (!companies) return [];

    const selectedCustomers = customers
      ? customers.filter((c) => selectedCompanyIds.includes(c.id))
      : [];

    return [...companies, ...selectedCustomers];
  }, [companies, customers, selectedCompanyIds]);

  const filteredAdaptedCustomers = adaptedCustomers.filter(
    (customer) =>
      !mergedCompanies.some((company) => company.id === customer.value)
  );

  const updateProductCardRequest = async () => {
    try {
      const companyIds = mergedCompanies.map((company) => company.id);
      await updateProductCard({
        id: values.id!,
        name: values.name!,
        process_steps: reorderingIds,
        companytoproductcards: companyIds,
      }).unwrap();
      api.success({
        message: t("product-card.success-title"),
        description: t("product-card.success-update-msg"),
      });

      await refetch();
      setSelectedCompanyIds([]);
      closeModal();
    } catch (error) {
      const description = handleRequestError(error, t("product-card.error"));

      api.error({
        message: t("product-card.error"),
        description,
      });
    }
  };

  const orderColumns = useGetSimpleOrderTableColumns({
    showDeleteOrderModal,
  });

  return (
    <>
      {contextHolder}
      <Modal
        title={t("product-card.edit-card-title")}
        open={isOpenModal}
        width={ModalSizeValue.XL_MEDIUM}
        okButtonProps={{
          disabled: !isValid,
          style: {
            background: `${themeColor}`,
          },
        }}
        onCancel={closeModal}
        onOk={() => {
          updateProductCardRequest();
        }}
      >
        {isLoadingProcessSteps ? (
          <Loader />
        ) : (
          <>
            <Title level={5} className="mb-1.25">
              {t("product-card.card-name")}
            </Title>

            <FormInput
              value={values.name}
              fieldName="name"
              placeholder={t("product-card.name-placeholder")}
              handleChange={handleChange}
              handleBlur={handleBlur}
              error={errors.name}
              isError={!!errors.name}
            />

            {isLoadingCompanies ? (
              <Loader />
            ) : (
              <>
                <div>
                  <Button
                    type="primary"
                    style={{ backgroundColor: themeColor }}
                    className="mt-2.5"
                    onClick={showCompanyModal}
                  >
                    {t("Add Customer")}
                  </Button>
                </div>
                <Title className="mb-3.75 mt-6" level={5}>
                  {t("companies.companies-table-title")}
                </Title>
                <Table
                  dataSource={mergedCompanies}
                  columns={companyColumns}
                  rowKey="id"
                  pagination={false}
                />
              </>
            )}

            {isLoadingProcessSteps ? (
              <Loader />
            ) : (
              <>
                <Button
                  type="primary"
                  style={{ backgroundColor: themeColor }}
                  className="mt-8 mb-2"
                  onClick={showProcessStepsModal}
                >
                  {t("product-card.add-step")}
                </Button>
                <Title className="mb-3.75" level={5}>
                  {t("product-card.process-steps")}
                </Title>
                <ProcessStepsTable
                  processSteps={processSteps}
                  setReorderingIds={setReorderingIds}
                />
              </>
            )}

            {isLoadingOrders ? (
              <Loader />
            ) : (
              <div className="w-full">
                <Title className="mb-3.75 mt-6" level={5}>
                  {t("orders.orders-table-title")}
                </Title>
                <Table
                  dataSource={Array.isArray(orders) ? orders : []}
                  columns={orderColumns}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: true }}
                />
              </div>
            )}

            <ProcessStepToTheCardActionsModal
              isOpenModal={isOpenAddProcessStepModal}
              hideProcessStepsModal={hideProcessStepsModal}
              adaptedSteps={adaptedSteps}
            />

            <AddCustomerToTheCardModal
              isOpenModal={isOpenAddCompanyModal}
              hideCustomerModal={hideCompanyModal}
              adaptedCustomers={filteredAdaptedCustomers}
              setSelectedCompanyIds={setSelectedCompanyIds}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export { EditProductCardModal };
